import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  APIProvider,
  Map,
  AdvancedMarker,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { useLocation } from "@/context/LocationContext";
import { useLang } from "@/context/LangContext";
import { X, Navigation2, MapPin, Trash2, ChevronLeft, Search } from "lucide-react";
import { toast } from "sonner";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const MAP_ID = "jayek_picker_map";
const STORAGE_KEY = "jayek_saved_addresses";

function loadSaved() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch { return []; }
}
function persistSaved(list) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

/* ─── Jahez-style home pin (orange teardrop + gold ring + house) ─── */
function JayekPin() {
  return (
    <div style={{ position: "relative", width: 46, height: 54, filter: "drop-shadow(0 6px 10px rgba(0,0,0,0.28))" }}>
      <div style={{ position: "absolute", inset: 0, background: "#E05D3A", borderRadius: "50% 50% 50% 0", transform: "rotate(-45deg)", border: "2.5px solid #FFFFFF" }} />
      <div style={{ position: "absolute", top: 5, left: "50%", transform: "translateX(-50%)", width: 34, height: 34, background: "#FFFFFF", borderRadius: "50%", border: "2.5px solid #F8B73E", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E05D3A" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
      </div>
    </div>
  );
}

/* ─── Search box with Places Autocomplete (uses google.maps.places library) ─── */
function PlacesSearch({ onPick, lang }) {
  const placesLib = useMapsLibrary("places");
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [open, setOpen] = useState(false);
  const sessionTokenRef = useRef(null);
  const autocompleteRef = useRef(null);
  const placesServiceRef = useRef(null);

  useEffect(() => {
    if (!placesLib) return;
    autocompleteRef.current = new placesLib.AutocompleteService();
    // PlacesService needs a DOM element (attribution element) — use a hidden div
    const attrEl = document.createElement("div");
    placesServiceRef.current = new placesLib.PlacesService(attrEl);
    sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
  }, [placesLib]);

  // Fetch predictions (debounced)
  useEffect(() => {
    if (!query.trim() || !autocompleteRef.current) {
      setPredictions([]);
      return;
    }
    const t = setTimeout(() => {
      autocompleteRef.current.getPlacePredictions(
        {
          input: query,
          componentRestrictions: { country: "sa" }, // Saudi Arabia only
          language: lang === "ar" ? "ar" : "en",
          sessionToken: sessionTokenRef.current,
        },
        (results, status) => {
          if (status === "OK" && results) {
            setPredictions(results);
            setOpen(true);
          } else {
            setPredictions([]);
          }
        }
      );
    }, 250);
    return () => clearTimeout(t);
  }, [query, lang]);

  const onSelect = (prediction) => {
    if (!placesServiceRef.current) return;
    placesServiceRef.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ["geometry", "formatted_address", "name", "address_components"],
        sessionToken: sessionTokenRef.current,
        language: lang === "ar" ? "ar" : "en",
      },
      (place, status) => {
        if (status === "OK" && place?.geometry?.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          onPick({
            lat,
            lng,
            formatted_address: place.formatted_address || prediction.description,
            name: place.name,
            address_components: place.address_components,
          });
          setQuery(place.name || prediction.structured_formatting?.main_text || "");
          setOpen(false);
          // Fresh session token after selection (Google pricing)
          sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
          inputRef.current?.blur();
        }
      }
    );
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B6B] pointer-events-none" />
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => predictions.length > 0 && setOpen(true)}
          placeholder={lang === "ar" ? "ابحث عن حي، شارع، مطعم أو معلم…" : "Search neighborhood, street, place…"}
          className="w-full ps-10 pe-10 py-3 rounded-full border border-[#EAEAEA] bg-white shadow-md focus:border-[#E05D3A] focus:ring-2 focus:ring-[#E05D3A]/20 outline-none text-[14px] placeholder:text-[#BDBDBD]"
          data-testid="places-search-input"
        />
        {query && (
          <button
            type="button"
            onClick={() => { setQuery(""); setPredictions([]); setOpen(false); }}
            className="absolute end-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-[#F5F5F5] flex items-center justify-center"
            aria-label="clear"
          >
            <X className="w-4 h-4 text-[#6B6B6B]" />
          </button>
        )}
      </div>

      {open && predictions.length > 0 && (
        <div className="absolute top-full inset-x-0 mt-2 bg-white rounded-2xl border border-[#EAEAEA] shadow-2xl max-h-[60vh] overflow-y-auto z-[600]">
          {predictions.map((p) => (
            <button
              type="button"
              key={p.place_id}
              onClick={() => onSelect(p)}
              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-[#FFF1EA] active:bg-[#FFE3D3] transition text-start border-b last:border-b-0 border-[#F5F5F5]"
              data-testid={`prediction-${p.place_id}`}
            >
              <div className="w-9 h-9 rounded-full bg-[#FFF1EA] flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 text-[#E05D3A]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[14px] text-[#1A1A1A] truncate">
                  {p.structured_formatting?.main_text || p.description}
                </p>
                <p className="text-[12px] text-[#6B6B6B] truncate mt-0.5">
                  {p.structured_formatting?.secondary_text || ""}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Hook: reverse-geocode a lat/lng using Google Geocoder ─── */
function useReverseGeocode(coords, lang) {
  const geocodingLib = useMapsLibrary("geocoding");
  const [result, setResult] = useState({ formatted: "", neighborhood: "", street: "", loading: false });

  useEffect(() => {
    if (!geocodingLib || !coords) return;
    const geocoder = new geocodingLib.Geocoder();
    let cancelled = false;
    geocoder.geocode(
      { location: coords, language: lang === "ar" ? "ar" : "en", region: "SA" },
      (results, status) => {
        if (cancelled) return;
        if (status === "OK" && results?.[0]) {
          const r = results[0];
          const comps = r.address_components || [];
          const neighborhood =
            comps.find((c) => c.types.includes("neighborhood"))?.long_name ||
            comps.find((c) => c.types.includes("sublocality_level_1"))?.long_name ||
            comps.find((c) => c.types.includes("sublocality"))?.long_name ||
            "";
          const street = comps.find((c) => c.types.includes("route"))?.long_name || "";
          setResult({
            formatted: r.formatted_address || "",
            neighborhood,
            street,
            loading: false,
          });
        } else {
          setResult({ formatted: "", neighborhood: "", street: "", loading: false });
        }
      }
    );
    return () => { cancelled = true; };
  }, [coords, lang, geocodingLib]);

  return result;
}

/* ─── Inner content (must be inside <APIProvider>) ─── */
function PickerInner({ location, setLocation, detectCityFromCoords, gpsCity, locateGps, lang, navigate }) {
  const [coords, setCoords] = useState({ lat: location.lat, lng: location.lng });
  const [pinName, setPinName] = useState("");
  const [savedList, setSavedList] = useState(loadSaved);
  const [showSheet, setShowSheet] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [form, setForm] = useState({ label: "", street: "", building: "", floor: "", unit: "", notes: "" });
  const mapRef = useRef(null);
  const map = useMap();

  // Keep mapRef in sync
  useEffect(() => { if (map) mapRef.current = map; }, [map]);

  const reverse = useReverseGeocode(coords, lang);
  const displayAddress = pinName || reverse.formatted;

  // Try GPS on first open (silent if denied)
  useEffect(() => {
    locateGps().then((ll) => {
      setCoords(ll);
      setPinName("");
      if (map) { map.panTo(ll); map.setZoom(17); }
    }).catch(() => {});
  }, [map, locateGps]);

  const onPick = useCallback((ll) => {
    setCoords({ lat: ll.lat, lng: ll.lng });
    setPinName("");
  }, []);

  const onSearchPick = useCallback(({ lat, lng, formatted_address, name }) => {
    const ll = { lat, lng };
    setCoords(ll);
    setPinName(name && name !== formatted_address ? name : "");
    if (map) { map.panTo(ll); map.setZoom(17); }
  }, [map]);

  const requestGps = () => {
    if (!navigator.geolocation) {
      toast.error(lang === "ar" ? "المتصفح لا يدعم GPS" : "GPS not supported");
      return;
    }
    setGpsLoading(true);
    locateGps()
      .then((ll) => {
        setCoords(ll);
        setPinName("");
        if (map) { map.panTo(ll); map.setZoom(17); }
        setGpsLoading(false);
        toast.success(lang === "ar" ? "تم تحديد موقعك" : "Located");
      })
      .catch((err) => {
        setGpsLoading(false);
        const msg = err?.code === 1
          ? (lang === "ar" ? "رفض إذن الموقع. اسمح بالوصول من إعدادات المتصفح." : "Permission denied.")
          : (lang === "ar" ? "تعذّر الوصول للموقع." : "Position unavailable.");
        toast.error(msg, { duration: 6000 });
      });
  };

  const detectedCity = detectCityFromCoords(coords.lat, coords.lng);
  const gpsCityName = gpsCity ? (lang === "ar" ? gpsCity.name_ar : gpsCity.name_en) : "";
  const outsideCity = !!gpsCity && detectedCity.id !== gpsCity.id;

  // Users may only pick delivery locations inside the city their device is physically in.
  const guardCity = (cityId) => {
    if (!gpsCity) {
      toast.error(lang === "ar" ? "فعّل خدمة الموقع (GPS) لتحديد مدينتك الحالية أولاً" : "Enable GPS so we can detect your current city first", { duration: 6000 });
      requestGps();
      return false;
    }
    if (cityId !== gpsCity.id) {
      toast.error(
        lang === "ar" ? `لا يمكنك اختيار موقع خارج مدينتك الحالية (${gpsCityName})` : `You can only pick a location inside your current city (${gpsCityName})`,
        { duration: 6000 }
      );
      return false;
    }
    return true;
  };

  const confirm = () => {
    if (!guardCity(detectedCity.id)) return;
    const street = reverse.street || pinName || "";
    const district_ar = reverse.neighborhood || detectedCity.districts[0].ar;
    const district_en = reverse.neighborhood || detectedCity.districts[0].en;
    setLocation({
      city_id: detectedCity.id,
      city_ar: detectedCity.name_ar,
      city_en: detectedCity.name_en,
      district_ar,
      district_en,
      lat: coords.lat,
      lng: coords.lng,
      street: street,
      formatted_address: reverse.formatted || displayAddress,
    });
    toast.success(lang === "ar" ? "تم حفظ الموقع" : "Location saved");
    navigate(-1);
  };

  const saveAddress = (e) => {
    e?.preventDefault();
    if (!form.label.trim()) {
      toast.error(lang === "ar" ? "أدخل تسمية العنوان" : "Enter an address label");
      return;
    }
    if (!guardCity(detectedCity.id)) return;
    const entry = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      label: form.label.trim(),
      street: form.street.trim() || reverse.street,
      building: form.building.trim(),
      floor: form.floor.trim(),
      unit: form.unit.trim(),
      notes: form.notes.trim(),
      lat: coords.lat,
      lng: coords.lng,
      city_id: detectedCity.id,
      city_ar: detectedCity.name_ar,
      city_en: detectedCity.name_en,
      district_ar: reverse.neighborhood || detectedCity.districts[0].ar,
      district_en: reverse.neighborhood || detectedCity.districts[0].en,
      auto_address: reverse.formatted,
      created_at: Date.now(),
    };
    const next = [entry, ...savedList];
    setSavedList(next);
    persistSaved(next);
    setForm({ label: "", street: "", building: "", floor: "", unit: "", notes: "" });
    setShowSheet(false);
    toast.success(lang === "ar" ? "تم حفظ العنوان" : "Address saved");
  };

  const deleteSaved = (id, e) => {
    e?.stopPropagation();
    const next = savedList.filter((x) => x.id !== id);
    setSavedList(next);
    persistSaved(next);
    toast.success(lang === "ar" ? "تم الحذف" : "Deleted");
  };

  const pickSaved = (a) => {
    const cityHere = detectCityFromCoords(a.lat, a.lng);
    if (!guardCity(cityHere.id)) return;
    const ll = { lat: a.lat, lng: a.lng };
    setCoords(ll);
    setPinName(a.label || "");
    if (map) { map.panTo(ll); map.setZoom(17); }
    setLocation({
      city_id: cityHere.id,
      city_ar: cityHere.name_ar,
      city_en: cityHere.name_en,
      district_ar: a.district_ar,
      district_en: a.district_en,
      lat: a.lat,
      lng: a.lng,
      street: a.street,
      formatted_address: a.auto_address,
    });
    toast.success(lang === "ar" ? `تم اختيار: ${a.label}` : `Picked: ${a.label}`);
    navigate(-1);
  };

  return (
    <div className="fixed inset-0 bg-white flex flex-col z-[9999]">
      {/* Header (respects iOS status-bar safe area) */}
      <div
        className="relative bg-white border-b border-[#EAEAEA] flex items-center justify-center"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 14px)", paddingBottom: "14px" }}
      >
        <button
          onClick={() => navigate(-1)}
          className="absolute start-4 bottom-3 w-8 h-8 rounded-full bg-[#F2F2F2] hover:bg-[#EAEAEA] flex items-center justify-center"
          data-testid="loc-close"
          aria-label="close"
        >
          <X className="w-5 h-5 text-[#1A1A1A]" />
        </button>
        <h1 className="font-bold text-[16px] text-[#1A1A1A]">
          {lang === "ar" ? "اختر موقعك" : "Pick your location"}
        </h1>
      </div>

      {/* Map area with floating search */}
      <div className="relative flex-1 min-h-0">
        {/* Search bar (floating) */}
        <div className="absolute top-3 inset-x-3 z-[500]">
          <PlacesSearch onPick={onSearchPick} lang={lang} />
        </div>

        <Map
          defaultCenter={coords}
          defaultZoom={15}
          mapId={MAP_ID}
          gestureHandling="greedy"
          disableDefaultUI={true}
          zoomControl={false}
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={false}
          clickableIcons={true}
          onClick={(e) => { if (e.detail?.latLng) onPick(e.detail.latLng); }}
          style={{ width: "100%", height: "100%" }}
        >
          <AdvancedMarker
            position={coords}
            draggable
            onDragEnd={(e) => {
              const ll = e.latLng;
              if (ll) onPick({ lat: ll.lat(), lng: ll.lng() });
            }}
          >
            <JayekPin />
          </AdvancedMarker>
        </Map>

        {/* My-location floating button */}
        <button
          type="button"
          onClick={requestGps}
          disabled={gpsLoading}
          className="absolute start-4 bottom-4 z-[400] w-12 h-12 rounded-full bg-white shadow-xl border border-[#EAEAEA] flex items-center justify-center hover:bg-[#FFF1EA] active:scale-95 transition disabled:opacity-60"
          data-testid="use-gps-btn"
          aria-label={lang === "ar" ? "موقعي الحالي" : "My location"}
        >
          <Navigation2
            className={`w-5 h-5 ${gpsLoading ? "animate-pulse" : ""} text-[#E05D3A]`}
            style={{ transform: "rotate(45deg)" }}
            fill="#E05D3A"
          />
        </button>
      </div>

      {/* Bottom card (compact, Jahez-style, respects bottom safe area) */}
      <div
        className="bg-white border-t border-[#EAEAEA] rounded-t-2xl shadow-[0_-4px_16px_rgba(0,0,0,0.06)] px-5 pt-4 z-[500]"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 14px)" }}
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="flex-1 min-w-0 flex items-start gap-2">
            <MapPin className="w-5 h-5 text-[#1A1A1A] flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="font-bold text-[15px] text-[#1A1A1A] truncate" data-testid="picker-city">
                {pinName || (lang === "ar" ? detectedCity.name_ar : detectedCity.name_en)}
              </p>
              <p className="text-[13px] text-[#6B6B6B] mt-0.5 line-clamp-2" data-testid="picker-address">
                {reverse.loading
                  ? (lang === "ar" ? "جاري تحديد العنوان…" : "Resolving address…")
                  : (reverse.formatted || (lang === "ar" ? "اسحب الدبوس أو ابحث عن مكان" : "Drag the pin or search"))}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowSheet(true)}
            className="text-[13px] font-semibold text-[#E05D3A] hover:underline whitespace-nowrap pt-0.5"
            data-testid="save-location-link"
          >
            {lang === "ar" ? "حفظ الموقع" : "Save location"}
          </button>
        </div>

        {outsideCity && (
          <div className="mb-3 rounded-xl bg-[#FFF1EA] border border-[#F5C9B8] px-3 py-2 text-[12.5px] text-[#C24B2C] font-semibold" data-testid="outside-city-warning">
            {lang === "ar"
              ? `هذا الموقع في ${detectedCity.name_ar} — التوصيل متاح فقط داخل مدينتك الحالية (${gpsCityName})`
              : `This spot is in ${detectedCity.name_en} — delivery is only available inside your current city (${gpsCityName})`}
          </div>
        )}
        {!gpsCity && (
          <div className="mb-3 rounded-xl bg-[#F7F7F7] border border-[#EAEAEA] px-3 py-2 text-[12.5px] text-[#6B6B6B] font-semibold" data-testid="gps-required-notice">
            {lang === "ar" ? "فعّل خدمة الموقع (GPS) لتحديد مدينتك الحالية" : "Enable GPS to detect your current city"}
          </div>
        )}
        <button
          type="button"
          onClick={confirm}
          className={`w-full rounded-full active:scale-[0.99] transition text-white font-bold text-[15px] py-3.5 ${outsideCity || !gpsCity ? "bg-[#C9C9C9] hover:bg-[#BDBDBD]" : "bg-[#E05D3A] hover:bg-[#C24B2C]"}`}
          data-testid="confirm-location-btn"
        >
          {lang === "ar" ? "تأكيد موقع التوصيل" : "Confirm delivery location"}
        </button>

        {/* Saved addresses entry — opens the saved list sheet */}
        <button
          type="button"
          onClick={() => setShowSaved(true)}
          className="w-full text-center text-[14px] font-semibold text-[#E05D3A] hover:underline py-3"
          data-testid="saved-addresses-link"
        >
          {lang === "ar" ? "العناوين المحفوظة" : "Saved addresses"}
          {savedList.length > 0 ? ` (${savedList.length})` : ""}
        </button>
      </div>

      {/* Save-address Bottom Sheet */}
      {showSheet && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-[10000]"
            onClick={() => setShowSheet(false)}
            data-testid="sheet-backdrop"
          />
          <div className="fixed inset-x-0 bottom-0 z-[10001] bg-white rounded-t-3xl shadow-2xl max-h-[88vh] overflow-y-auto animate-in slide-in-from-bottom duration-200">
            <div className="sticky top-0 bg-white pt-3 pb-2 flex justify-center">
              <div className="w-10 h-1 rounded-full bg-[#E0E0E0]" />
            </div>
            <form onSubmit={saveAddress} className="px-5 pb-6 space-y-4">
              <div>
                <label className="block text-[14px] font-semibold text-[#1A1A1A] mb-2">
                  {lang === "ar" ? "تسمية العنوان" : "Address label"}
                </label>
                <div className="flex gap-2 mb-2 flex-wrap">
                  {(lang === "ar" ? ["المنزل", "العمل", "أخرى"] : ["Home", "Work", "Other"]).map((chip) => (
                    <button
                      type="button"
                      key={chip}
                      onClick={() => setForm({ ...form, label: chip })}
                      className={`px-4 py-1.5 rounded-full text-[13px] font-semibold border transition ${form.label === chip ? "bg-[#E05D3A] text-white border-[#E05D3A]" : "bg-white text-[#1A1A1A] border-[#EAEAEA] hover:border-[#E05D3A]"}`}
                      data-testid={`label-chip-${chip}`}
                    >
                      {chip}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder={lang === "ar" ? "مثال، المنزل، العمل" : "e.g., Home, Work"}
                  autoFocus
                  className="w-full px-4 py-3.5 rounded-xl border border-[#EAEAEA] bg-white focus:border-[#E05D3A] focus:ring-2 focus:ring-[#E05D3A]/20 outline-none text-[14px] placeholder:text-[#BDBDBD]"
                  data-testid="addr-label"
                />
              </div>

              <Field label={lang === "ar" ? "اسم أو رقم الشارع" : "Street"} placeholder={reverse.street || (lang === "ar" ? "مثال، شارع الثمامة" : "e.g., Al Thumamah St")} value={form.street} onChange={(v) => setForm({ ...form, street: v })} testId="addr-street" />
              <Field label={lang === "ar" ? "اسم أو رقم المبنى" : "Building"} placeholder={lang === "ar" ? "مثال، 500" : "e.g., 500"} value={form.building} onChange={(v) => setForm({ ...form, building: v })} testId="addr-building" />
              <Field label={lang === "ar" ? "رقم الطابق" : "Floor"} placeholder={lang === "ar" ? "مثال، 3" : "e.g., 3"} value={form.floor} onChange={(v) => setForm({ ...form, floor: v })} testId="addr-floor" />
              <Field label={lang === "ar" ? "رقم الشقة" : "Unit"} placeholder={lang === "ar" ? "مثال، 34" : "e.g., 34"} value={form.unit} onChange={(v) => setForm({ ...form, unit: v })} testId="addr-unit" />
              <Field label={lang === "ar" ? "تفاصيل إضافية" : "Notes"} placeholder={lang === "ar" ? "مثال، رمز الدخول 3409" : "e.g., entry code 3409"} value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testId="addr-notes" />

              <div className="pt-2 space-y-3">
                <button type="submit" className="w-full rounded-full bg-[#E05D3A] hover:bg-[#C24B2C] active:scale-[0.99] transition text-white font-bold text-[15px] py-4" data-testid="save-address-btn">
                  {lang === "ar" ? "حفظ العنوان" : "Save address"}
                </button>
                <button type="button" onClick={() => setShowSheet(false)} className="w-full text-center text-[#E05D3A] font-semibold text-[14px] py-2" data-testid="cancel-address-btn">
                  {lang === "ar" ? "إلغاء" : "Cancel"}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* Saved-addresses Bottom Sheet */}
      {showSaved && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-[10000]"
            onClick={() => setShowSaved(false)}
            data-testid="saved-sheet-backdrop"
          />
          <div
            className="fixed inset-x-0 bottom-0 z-[10001] bg-white rounded-t-3xl shadow-2xl max-h-[80vh] overflow-y-auto animate-in slide-in-from-bottom duration-200"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }}
          >
            <div className="sticky top-0 bg-white pt-3 pb-2 flex justify-center">
              <div className="w-10 h-1 rounded-full bg-[#E0E0E0]" />
            </div>
            <div className="px-5 pb-2">
              <h2 className="font-bold text-[16px] text-[#1A1A1A] mb-3" data-testid="saved-addresses-title">
                {lang === "ar" ? "العناوين المحفوظة" : "Saved addresses"}
              </h2>
              {savedList.length === 0 ? (
                <div className="py-10 text-center">
                  <div className="w-14 h-14 rounded-full bg-[#FFF1EA] flex items-center justify-center mx-auto mb-3">
                    <MapPin className="w-6 h-6 text-[#E05D3A]" />
                  </div>
                  <p className="text-[14px] text-[#6B6B6B]">
                    {lang === "ar" ? "لا توجد عناوين محفوظة بعد" : "No saved addresses yet"}
                  </p>
                  <button
                    type="button"
                    onClick={() => { setShowSaved(false); setShowSheet(true); }}
                    className="mt-4 inline-flex rounded-full bg-[#E05D3A] text-white font-semibold text-[14px] px-6 py-2.5"
                    data-testid="add-first-address-btn"
                  >
                    {lang === "ar" ? "إضافة عنوان" : "Add address"}
                  </button>
                </div>
              ) : (
                <ul className="space-y-2">
                  {savedList.map((a) => (
                    <li key={a.id}>
                      <button
                        type="button"
                        onClick={() => { setShowSaved(false); pickSaved(a); }}
                        className="w-full flex items-center gap-3 px-3 py-3 rounded-xl border border-[#EAEAEA] hover:bg-[#FFF1EA] hover:border-[#E05D3A]/40 active:scale-[0.99] transition text-start"
                        data-testid={`saved-addr-${a.id}`}
                      >
                        <div className="w-9 h-9 rounded-full bg-[#FFF1EA] flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-4 h-4 text-[#E05D3A]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[14px] text-[#1A1A1A] truncate">{a.label || a.street}</p>
                          <p className="text-[12px] text-[#6B6B6B] truncate">
                            {[a.street, lang === "ar" ? a.city_ar : a.city_en, a.floor && `${lang === "ar" ? "طابق" : "floor"} ${a.floor}`, a.unit && `${lang === "ar" ? "شقة" : "unit"} ${a.unit}`].filter(Boolean).join(" · ")}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => deleteSaved(a.id, e)}
                          className="w-8 h-8 rounded-full hover:bg-red-50 flex items-center justify-center flex-shrink-0"
                          aria-label="delete"
                          data-testid={`delete-addr-${a.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-[#E05D3A]" />
                        </button>
                        <ChevronLeft className="w-4 h-4 text-[#BDBDBD] flex-shrink-0 rtl:rotate-180" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Field({ label, placeholder, value, onChange, testId }) {
  return (
    <div>
      <label className="block text-[14px] font-semibold text-[#1A1A1A] mb-2">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-3.5 rounded-xl border border-[#EAEAEA] bg-white focus:border-[#E05D3A] focus:ring-2 focus:ring-[#E05D3A]/20 outline-none text-[14px] placeholder:text-[#BDBDBD]"
        data-testid={testId}
      />
    </div>
  );
}

export default function LocationPicker() {
  const { location, setLocation, detectCityFromCoords, gpsCity, locateGps } = useLocation();
  const { lang } = useLang();
  const navigate = useNavigate();

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center p-6 z-[9999] text-center">
        <div>
          <p className="font-bold text-[16px] text-[#1A1A1A] mb-2">
            {lang === "ar" ? "خدمة الخرائط غير متاحة" : "Maps service unavailable"}
          </p>
          <p className="text-[13px] text-[#6B6B6B]">
            {lang === "ar" ? "مفتاح Google Maps غير معدّ." : "Google Maps API key is missing."}
          </p>
          <button onClick={() => navigate(-1)} className="mt-4 px-6 py-2 rounded-full bg-[#E05D3A] text-white font-semibold">
            {lang === "ar" ? "رجوع" : "Back"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <APIProvider
      apiKey={GOOGLE_MAPS_API_KEY}
      libraries={["places", "geocoding"]}
      language={lang === "ar" ? "ar" : "en"}
      region="SA"
    >
      <PickerInner
        location={location}
        setLocation={setLocation}
        detectCityFromCoords={detectCityFromCoords}
        gpsCity={gpsCity}
        locateGps={locateGps}
        lang={lang}
        navigate={navigate}
      />
    </APIProvider>
  );
}
